import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { areaPath, axisTicks, bars, donutDashes, linePath, linePoints, niceMax, spark, type Frame } from '../lib/liveDash'

// The Live dashboard's charts, drawn as SVG by hand: no chart library ships with an on-premise build.

const FRAME: Frame = { w: 720, h: 236, l: 34, r: 40, t: 20, b: 28 }

/** The drawn width of a chart, so it is laid out at 1:1 and its words keep their size on any screen. */
function useChartWidth(fallback: number) {
  const [el, setEl] = useState<SVGSVGElement | null>(null)
  const [w, setW] = useState(fallback)
  useEffect(() => {
    if (!el) return
    const measure = () => setW((old) => Math.round(el.getBoundingClientRect().width) || old)
    measure()
    const watch = new ResizeObserver(measure)
    watch.observe(el)
    return () => watch.disconnect()
  }, [el])
  return [useCallback((node: SVGSVGElement | null) => setEl(node), []), w] as const
}

/** A top for the axis split into four whole steps, the smallest that holds `v`. */
function scaleMax(v: number): number {
  const need = Math.max(1, v) / 4
  const p = 10 ** Math.floor(Math.log10(need))
  const step = [1, 2, 3, 4, 5, 6, 8, 10].map((m) => m * p).find((s) => s >= need && Number.isInteger(s)) ?? 10 * p
  return Math.max(4, step * 4)
}

/** People waiting (left axis) and the longest wait in minutes (right axis), with the target as a dashed line. */
export function WaitChart({
  people,
  longestMin,
  targetMin,
  times,
  nowLabel,
  peopleAxis,
  minAxis,
  label,
  hover,
}: {
  people: number[]
  longestMin: number[]
  targetMin?: number
  times: string[]
  nowLabel: string
  peopleAxis: string
  minAxis: string
  label: string
  hover: (i: number) => string
}) {
  const [ref, width] = useChartWidth(FRAME.w)
  const F: Frame = { ...FRAME, w: width }
  const maxP = scaleMax(Math.max(...people, 0))
  const maxL = scaleMax(Math.max(...longestMin, targetMin ?? 0, 0))
  const base = F.h - F.b
  const ph = base - F.t
  const pp = linePoints(people, F, maxP)
  const lp = linePoints(longestMin, F, maxL)
  const col = people.length > 1 ? (F.w - F.l - F.r) / (people.length - 1) : 0
  const last = pp.length - 1
  const ty = targetMin ? F.t + ph - (targetMin / maxL) * ph : undefined
  return (
    <svg ref={ref} className="fl-chart" viewBox={`0 0 ${F.w} ${F.h}`} role="img" aria-label={label}>
      <text x={F.l - 8} y={F.t - 8} textAnchor="end">
        {peopleAxis}
      </text>
      <text x={F.w - F.r + 8} y={F.t - 8}>
        {minAxis}
      </text>
      {[0, 1, 2, 3, 4].map((i) => {
        const y = F.t + ph - (i / 4) * ph
        return (
          <g key={i}>
            <line x1={F.l} x2={F.w - F.r} y1={y} y2={y} className="fl-chart__grid" />
            <text x={F.l - 8} y={y + 4} textAnchor="end">
              {(maxP * i) / 4}
            </text>
            <text x={F.w - F.r + 8} y={y + 4}>
              {(maxL * i) / 4}
            </text>
          </g>
        )
      })}
      {axisTicks(people.length).map((i) => (
        <text key={i} x={pp[i]?.x ?? 0} y={F.h - 8} textAnchor="middle">
          {i === last ? nowLabel : times[i]}
        </text>
      ))}
      {ty !== undefined && <line x1={F.l} x2={F.w - F.r} y1={ty} y2={ty} className="fl-chart__target" />}
      {pp.length > 0 && (
        <>
          <path d={areaPath(pp, base)} className="fl-chart__area" />
          <path d={linePath(pp)} className="fl-chart__people" />
          <path d={linePath(lp)} className="fl-chart__longest" />
          <line x1={pp[last]!.x} x2={pp[last]!.x} y1={F.t} y2={base} className="fl-chart__now" />
          <circle cx={pp[last]!.x} cy={pp[last]!.y} r={3.5} className="fl-chart__dot" />
          {lp[last] && <circle cx={lp[last].x} cy={lp[last].y} r={3} className="fl-chart__dot fl-chart__dot--muted" />}
        </>
      )}
      {pp.map((p, i) => (
        <g key={i} className="fl-chart__hv">
          <title>{hover(i)}</title>
          <rect x={p.x - col / 2} y={F.t} width={col} height={ph} fill="transparent" />
          <line x1={p.x} x2={p.x} y1={F.t} y2={base} />
        </g>
      ))}
    </svg>
  )
}

const BAR: Frame = { w: 440, h: 200, l: 30, r: 8, t: 12, b: 26 }

/** Columns per quarter hour, the current one solid. */
export function BarChart({ values, times, nowLabel, label, hover }: { values: number[]; times: string[]; nowLabel: string; label: string; hover: (i: number) => string }) {
  const [ref, width] = useChartWidth(BAR.w)
  const B: Frame = { ...BAR, w: width }
  const max = niceMax(Math.max(...values, 1))
  const ph = B.h - B.t - B.b
  const shapes = bars(values, B, max)
  const last = shapes.length - 1
  return (
    <svg ref={ref} className="fl-chart" viewBox={`0 0 ${B.w} ${B.h}`} role="img" aria-label={label}>
      {[0, 0.5, 1].map((f) => {
        const v = Math.round(max * f)
        const y = B.t + ph - (v / max) * ph
        return (
          <g key={f}>
            <line x1={B.l} x2={B.w - B.r} y1={y} y2={y} className="fl-chart__grid" />
            <text x={B.l - 8} y={y + 4} textAnchor="end">
              {v}
            </text>
          </g>
        )
      })}
      {shapes.map((s, i) => (
        <g key={i} className="fl-chart__bar" data-now={i === last || undefined}>
          <title>{hover(i)}</title>
          <rect x={s.x} y={B.t} width={s.slot} height={ph} fill="transparent" />
          {s.path && <path d={s.path} />}
        </g>
      ))}
      {axisTicks(values.length).map((i) => (
        <text key={i} x={shapes[i]?.centre ?? 0} y={B.h - 8} textAnchor="middle">
          {i === last ? nowLabel : times[i]}
        </text>
      ))}
    </svg>
  )
}

/** A KPI tile's small trend, stretched to the tile's width. */
export function Sparkline({ values }: { values: number[] }) {
  const s = spark(values)
  return (
    <svg className="fl-spark" viewBox="0 0 120 34" preserveAspectRatio="none" aria-hidden="true">
      {s.line && (
        <>
          <path d={s.area} className="fl-spark__area" />
          <path d={s.line} className="fl-spark__line" vectorEffect="non-scaling-stroke" />
        </>
      )}
    </svg>
  )
}

export interface DonutPart {
  label: string
  value: number
  /** The part's colour as a CSS value (a token). */
  colour: string
}

/** A ring split by share, its centre figure over it, and the key beside it. */
export function Donut({ parts, centre, sub, label }: { parts: DonutPart[]; centre: ReactNode; sub: string; label: string }) {
  const r = 52
  const dashes = donutDashes(
    parts.map((p) => p.value),
    r,
  )
  return (
    <div className="fl-donutbox">
      <div className="fl-donut">
        <svg viewBox="0 0 140 140" role="img" aria-label={label}>
          <circle r={r} cx={70} cy={70} fill="none" className="fl-donut__track" strokeWidth={14} />
          {parts.map((p, i) =>
            p.value > 0 ? (
              <circle
                key={p.label}
                r={r}
                cx={70}
                cy={70}
                fill="none"
                stroke={p.colour}
                strokeWidth={14}
                strokeDasharray={`${dashes[i]!.length} ${dashes[i]!.circumference}`}
                strokeDashoffset={dashes[i]!.offset}
                transform="rotate(-90 70 70)"
              >
                <title>{`${p.label}: ${p.value} (${dashes[i]!.share}%)`}</title>
              </circle>
            ) : null,
          )}
        </svg>
        <div className="fl-donut__c">
          <b>{centre}</b>
          <span>{sub}</span>
        </div>
      </div>
      <ul className="fl-key">
        {parts.map((p) => (
          <li key={p.label}>
            <i style={{ background: p.colour }} />
            {p.label}
            <b>{p.value}</b>
          </li>
        ))}
      </ul>
    </div>
  )
}

export interface HBar {
  key: string
  label: string
  value: number
  shown: string
  colour: string
  target?: number
  late?: boolean
  title?: string
}

/** Horizontal bars on one scale, with a target tick where one is set. */
export function HBars({ rows }: { rows: HBar[] }) {
  const max = Math.max(1, ...rows.map((r) => Math.max(r.value, r.target ?? 0)))
  return (
    <div className="fl-hbars">
      {rows.map((r) => (
        <div key={r.key} className="fl-hrow" title={r.title}>
          <span className="fl-hlabel">
            <i style={{ background: r.colour }} />
            {r.label}
          </span>
          <span className="fl-htrack">
            <span className="fl-hfill" style={{ width: `${(r.value / max) * 100}%`, background: r.colour }} />
            {r.target ? <span className="fl-target" style={{ left: `${Math.min(100, (r.target / max) * 100)}%` }} /> : null}
          </span>
          <b data-late={r.late || undefined}>{r.shown}</b>
        </div>
      ))}
    </div>
  )
}
